import { confirmSignUp, resendSignUpCode } from "aws-amplify/auth";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import OtpInput from "react-otp-input";
import { useToast } from "@landingClient/hooks/use-toast";
import Typography from "@mui/material/Typography";
import { Spinner } from "@uicomponents/ui/spinner";
import { useEffect, useRef, useState } from "react";
import { ButtonProps } from "@mui/material/Button";

const otpSchema = z.object({
  code: z
    .string()
    .min(6, "OTP code must be 6 digits")
    .max(6, "OTP code must be 6 digits"),
});

function cleanupOtpCode(code: string) {
  return code
    .replaceAll(" ", "")
    .replaceAll(/[^0-9]/g, "")
    .slice(0, 6);
}

const TimeoutButton = ({
  disabled,
  readyText,
  waitingText,
  timeout,
  onClick,
  ...buttonProps
}: {
  disabled: boolean;
  readyText: string;
  waitingText: string;
  timeout: number;
  onClick?: () => void;
} & ButtonProps) => {
  const [secondsLeft, setSecondsLeft] = useState(timeout);
  const [isDisabled, setIsDisabled] = useState(true);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isDisabled && secondsLeft > 0) {
      timerRef.current = setInterval(() => {
        setSecondsLeft((prevTime) => prevTime - 1);
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isDisabled, secondsLeft]);

  useEffect(() => {
    // When timeLeft reaches 0, enable the button and clear the interval
    if (secondsLeft <= 0) {
      setIsDisabled(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  }, [secondsLeft]);

  const handleClick = () => {
    // Execute the action if provided
    if (onClick) {
      onClick();
    }
    setSecondsLeft(timeout);
    setIsDisabled(true);
  };

  return (
    <Button
      disabled={disabled || isDisabled}
      onClick={handleClick}
      {...buttonProps}
    >
      {isDisabled
        ? waitingText.replace("{seconds}", secondsLeft.toString())
        : readyText}
    </Button>
  );
};

const OtpTab = ({
  username,
  destination,
  onSuccess,
}: {
  username: string;
  destination: string;
  onSuccess?: () => Promise<void>;
}) => {
  const { toast } = useToast();
  const {
    setValue,
    handleSubmit,
    watch,
    setError,
    formState: { isValid, isSubmitting, isSubmitSuccessful },
  } = useForm<z.infer<typeof otpSchema>>({
    resolver: zodResolver(otpSchema),
    mode: "all",
  });

  async function onOtpSubmit(data: { code: string }) {
    return confirmSignUp({
      username,
      confirmationCode: data.code,
    })
      .then(async () => {
        if (onSuccess) await onSuccess();
      })
      .catch((error) => {
        setValue("code", "");
        toast({
          title: "Error",
          description: error.message || "Failed to confirm code",
        });
        setError("code", {
          type: "manual",
          message: error.message || "Failed to confirm code",
        });
      });
  }

  return (
    <form onSubmit={handleSubmit(onOtpSubmit)}>
      <Box
        paddingTop={"20px"}
        display={"flex"}
        flexDirection={"column"}
        gap={"10px"}
      >
        <Box alignSelf={"center"} maxWidth={"250px"}>
          <Typography>Verification code has been sent to:</Typography>
          <Typography textAlign={"center"} fontWeight={"bold"}>
            {destination}
          </Typography>
        </Box>
        <OtpInput
          value={watch("code")}
          numInputs={6}
          onChange={(otpValue) => {
            setValue("code", cleanupOtpCode(otpValue), {
              shouldValidate: true,
            });
          }}
          renderSeparator={<span style={{ width: "8px" }}></span>}
          onPaste={(event) => {
            setValue(
              "code",
              cleanupOtpCode(event.clipboardData.getData("text")),
              { shouldValidate: true },
            );
            handleSubmit(onOtpSubmit)();
          }}
          inputType="text"
          renderInput={({ ref, ...props }) => (
            <TextField
              inputRef={ref}
              slotProps={{
                input: {
                  ...props,
                  style: { width: "40px", textAlign: "center" },
                },
              }}
            />
          )}
        />
        <Button
          disabled={!isValid || isSubmitting}
          fullWidth
          type="submit"
          variant="contained"
        >
          {isSubmitting ? <Spinner /> : "Validate"}
        </Button>
        <TimeoutButton
          disabled={isSubmitting || isSubmitSuccessful}
          fullWidth
          variant="outlined"
          readyText="Resend Code"
          waitingText="Resend Code in {seconds}s"
          timeout={30}
          onClick={() => {
            resendSignUpCode({ username });
          }}
        />
      </Box>
    </form>
  );
};

export default OtpTab;
